// Two-step admin sign-in: email + password (first factor), then the one-time
// code emailed to the admin (second factor). On success, routes to /admin.
import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { either } from '../lib/utils/utils';
import { VERSION } from '../VERSION';
import { postAdminLogin, postAdminVerify } from './adminApi';
import { ADMIN_PATH } from './adminPath';

const errStr = e => (!e ? 'Network error.' : typeof e === 'string' ? e : JSON.stringify(e));

const AdminLogin = ({ history }) => {
	const [step, setStep] = useState('creds');
	const [email, setEmail] = useState('');
	const [pwd, setPwd] = useState('');
	const [code, setCode] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');

	const submitCreds = () => {
		setBusy(true);
		setError('');
		postAdminLogin(email, pwd, VERSION).then(
			either(
				e => {
					setBusy(false);
					setError(errStr(e));
				},
				() => {
					setBusy(false);
					setStep('code');
				}
			)
		);
	};

	const submitCode = () => {
		setBusy(true);
		setError('');
		postAdminVerify(code).then(
			either(
				e => {
					setBusy(false);
					setError(errStr(e));
				},
				() => {
					setBusy(false);
					history.push(ADMIN_PATH);
				}
			)
		);
	};

	const onKey = fn => e => {
		if (e.key === 'Enter') fn();
	};

	return (
		<Box
			sx={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '70vh',
				px: 2
			}}>
			<Paper sx={{ p: 4, width: 360, maxWidth: '100%' }} elevation={3}>
				<Typography variant="h5" gutterBottom>
					Admin Sign In
				</Typography>

				{error ? (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				) : null}

				{step === 'creds' ? (
					<>
						<TextField
							fullWidth
							margin="normal"
							label="Email"
							type="email"
							value={email}
							onChange={e => setEmail(e.target.value)}
							onKeyPress={onKey(submitCreds)}
						/>
						<TextField
							fullWidth
							margin="normal"
							label="Password"
							type="password"
							value={pwd}
							onChange={e => setPwd(e.target.value)}
							onKeyPress={onKey(submitCreds)}
						/>
						<Button
							fullWidth
							sx={{ mt: 2 }}
							variant="contained"
							disabled={!email || !pwd || busy}
							onClick={submitCreds}>
							Continue
						</Button>
					</>
				) : (
					<>
						<Typography variant="body2" sx={{ mb: 1 }}>
							We emailed a verification code to {email}. Enter it
							below.
						</Typography>
						<TextField
							fullWidth
							margin="normal"
							label="Verification code"
							value={code}
							onChange={e => setCode(e.target.value)}
							onKeyPress={onKey(submitCode)}
							inputProps={{
								inputMode: 'numeric',
								autoComplete: 'one-time-code'
							}}
						/>
						<Button
							fullWidth
							sx={{ mt: 2 }}
							variant="contained"
							disabled={!code || busy}
							onClick={submitCode}>
							Verify &amp; Sign In
						</Button>
						<Button
							fullWidth
							sx={{ mt: 1 }}
							onClick={() => {
								setStep('creds');
								setCode('');
								setError('');
							}}>
							Start over
						</Button>
					</>
				)}

				{busy ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
						<CircularProgress size={32} />
					</Box>
				) : null}
			</Paper>
		</Box>
	);
};

export default withRouter(AdminLogin);
